/**
 * THE PRODUCT × STYLE MATRIX — pure, no React, no network, no imports beyond
 * the step list it filters.
 *
 * Brian: *"you can easily get the PRODUCT and STYLE fully built out and ready
 * for launch"*.
 *
 * Half of that is true and it is the half this file delivers: the catalog, the
 * matrix, the per-product step trimming and the per-style step sets are all
 * frontend work and they are all here. The other half is not ours to do, and
 * pretending otherwise is the failure mode this module exists to prevent.
 *
 * ⚠️ MEASURED AGAINST THE SHIPPED ENGINE, 2026-09-09, not assumed:
 *
 *   `sr-portal-backend/src/artwork/prompt-config.js`
 *       export const BEAST_BY_STYLE = { championship: 'championship-v1' }
 *
 *   `renderService.js#artworkConfig()`
 *       styles: Object.keys(BEAST_BY_STYLE).map(…)      ← ONE entry
 *       …and NO `products` key at all.
 *
 * So `/api/artwork/config` names exactly one style, ships no product list, and
 * `promptAssembler.js:126` refuses any style with no beast:
 *
 *       if (!beastFor(b.style)) problems.push(`no beast prompt for style …`)
 *
 * THE PRODUCT IS NOT VALIDATED, AND THAT IS WORSE THAN A 400. `normalizeBuild`
 * takes `product` as free text (`s(p.product) || DEFAULT_PRODUCT`) and drops it
 * into the beast's title line and its `{{PRODUCT}}` slot. Posting
 * `product: 'cufflinks'` therefore does not fail — it renders the CHAMPIONSHIP
 * RING beast, with its bezel, girdle, shank and palm clauses intact, and calls
 * the result "CHAMPIONSHIP CUFFLINKS". A rep would get five views of a ring
 * back from a cufflink build and have no way to tell the engine had ignored
 * them. An honest COMING chip is strictly better than a plausible wrong render,
 * so a product the beast cannot make is NOT selectable here.
 *
 * WHAT THAT MEANS FOR "READY FOR LAUNCH": every cell of this matrix is built,
 * and exactly one of them — `ring × championship` — can render today. Each
 * blocked cell names the ENGINE-SIDE artefact it is waiting on, and those items
 * are written up in `docs/BACKEND-HANDOVER.md`. The moment a beast is authored
 * the cell lights up from `/config` with no change to this file (see
 * `cellFor`), which is the whole point of keeping the rule here rather than in
 * a component.
 */

import { CHAMPIONSHIP_STEPS } from './steps.js'

/* ------------------------------------------------------------------ *
 * The steps every piece has, whatever it is
 * ------------------------------------------------------------------ */

/**
 * Four steps are not surfaces of the object — they are the LOCKED LAYER and the
 * presentation, and they are on every build of every product in every style.
 * `logoC` is the client crest, `title` is the keyed championship type, and
 * `background` carries the SR mark, the audience and the presentation line.
 * `complexity` is a render directive rather than a part of the piece.
 *
 * That is why trimming can never produce a flow with nothing required in it.
 */
export const UNIVERSAL_STEPS = ['logoC', 'title', 'complexity', 'background']

/** Every step key the Championship flow defines, for validation. */
export const ALL_STEP_KEYS = CHAMPIONSHIP_STEPS.map((s) => s.key)

/* ------------------------------------------------------------------ *
 * PRODUCTS — the physical object
 * ------------------------------------------------------------------ */

/**
 * ⚠️ PRODUCT AND STYLE WERE TANGLED, AND UNTANGLING THEM IS THE FIX.
 *
 * `lib/builders.js` lists eight real SR products, but three of them —
 * Class Ring, Replica Ring, Letter Winner Ring — are RINGS. They are not
 * different objects from a Championship Ring; they are different KINDS of ring,
 * which is exactly what a style is. The studio already half-knew this:
 * `letterwinner` was in the STYLE catalog and `letter-winner-ring` was in the
 * product list at the same time.
 *
 * `flows.js` recorded the consequence without naming the cause: `sr-grad`
 * (the class ring) had no honest destination because *"the Builder's style
 * catalog is championship / band / signet / letterwinner / corporate. None of
 * those IS a class ring."* A class ring is a ring in the class STYLE, and once
 * the axes are separated it is simply a cell.
 *
 * So: PRODUCT is the object (what the factory makes), STYLE is the kind of
 * piece (what the beast renders). Nothing that shipped is removed — every id
 * the studio offered before is still offered, and the three ring-products
 * become ring styles rather than disappearing.
 *
 * `surfaces` lists the step keys this object physically HAS, and it is the
 * whole of the per-product trimming rule. A pendant has no side shanks and no
 * palm-interior band, so those steps are not shown, not asked for, and not
 * posted — rather than being shown, left blank, and silently dropped.
 */
export const PRODUCTS = [
  {
    id: 'ring',
    label: 'Ring',
    what: 'The flagship. Bezel, girdle, two shanks, palm interior.',
    builderId: 'championship-ring',
    surfaces: ['bezel', 'girdle', 'shank1', 'shank2', 'palm', 'core', 'stones', 'metal', 'scale'],
  },
  {
    id: 'pendant',
    label: 'Pendant',
    what: 'A face and a reverse, hung from a bail. No shanks, no interior band.',
    builderId: 'championship-pendant',
    surfaces: ['bezel', 'core', 'stones', 'metal'],
  },
  {
    id: 'challenge-coin',
    label: 'Challenge Coin',
    what: 'Struck obverse and reverse. No stones — a coin is struck, not set.',
    builderId: 'challenge-coin',
    surfaces: ['bezel', 'core', 'metal'],
  },
  {
    id: 'lapel-pin',
    label: 'Lapel Pin',
    what: 'One small face on a post.',
    builderId: 'lapel-pin',
    surfaces: ['bezel', 'stones', 'metal'],
  },
  {
    id: 'stick-on-lapel',
    label: 'Stick-on Lapel',
    what: 'The adhesive-backed lapel piece. One face.',
    builderId: null,
    surfaces: ['bezel', 'stones', 'metal'],
  },
  {
    id: 'cufflinks',
    label: 'Cufflinks',
    what: 'A matched pair of faces on toggles.',
    builderId: null,
    surfaces: ['bezel', 'core', 'stones', 'metal'],
  },
  {
    id: 'bracelet',
    label: 'Bracelet',
    what: 'A centre plate with a flanking link either side — the one non-ring piece that really does have two side panels.',
    builderId: null,
    surfaces: ['bezel', 'shank1', 'shank2', 'core', 'stones', 'metal'],
  },
  {
    id: 'belt-buckle',
    label: 'Belt Buckle',
    what: 'A single large face with an engraved back.',
    builderId: null,
    surfaces: ['bezel', 'core', 'stones', 'metal'],
  },
  {
    /* Kept in the catalog on purpose. It is a real SR product
     * (`lib/builders.js#letterman-jacket`) and leaving it out would say SR does
     * not sell one. It is `renderable: false` because the artwork engine
     * renders JEWELRY: every beast slot it has — bezel, girdle, shank, palm,
     * stone setting, metal finish — describes a metal piece. A jacket needs its
     * own beast built around body, sleeves, chenille and patches, and that is
     * not a trim of the ring prompt. */
    id: 'letterman-jacket',
    label: 'Letterman Jacket',
    what: 'Apparel — body, sleeves, chenille letter, patches.',
    builderId: 'letterman-jacket',
    surfaces: [],
    renderable: false,
    blockedReason:
      'The artwork engine renders jewelry: every slot the beast has is a metal surface. A jacket needs its own beast, not a trim of the ring prompt.',
  },
]

export const PRODUCT_BY_ID = Object.fromEntries(PRODUCTS.map((p) => [p.id, p]))

/* ------------------------------------------------------------------ *
 * STYLES — the kind of piece
 * ------------------------------------------------------------------ */

/**
 * `drops` is the per-style step set, expressed as what the style does NOT use.
 *
 * ⚠️ `confirmed` IS THE HONEST FIELD HERE. Master Spec Section 12, open item #2
 * is *"the per-style step sets for Band / Signet / Letterwinner / Corporate"* —
 * still unanswered. So the four sets below are the studio's best reading of the
 * object (a band has no raised bezel and no distinct shanks; a signet is a flat
 * intaglio face and carries no stones), and they say so on screen rather than
 * presenting a guess as the spec.
 *
 * It costs nothing today, because none of these styles has a beast and so none
 * is selectable — but the day one lands, the flow it opens with is a decision
 * somebody made, and it should be visibly a provisional one until Brian
 * confirms it.
 *
 * THE REAL FIX IS BACKEND-SIDE and it is in the handover: `/config.scriptSteps`
 * is ONE global list, so the engine has no per-style step concept at all. When
 * it grows `styles[].scriptSteps`, `mergeSteps()` reads it and this table stops
 * being the authority (see `steps.js#mergeSteps`).
 */
export const STYLES = [
  {
    id: 'championship',
    label: 'Championship',
    what: 'The title piece. The full flow, and the reference every other style is a subset of.',
    drops: [],
    confirmed: true,
  },
  {
    id: 'band',
    label: 'Band',
    what: 'A continuous band. No raised bezel, no distinct shanks.',
    drops: ['bezel', 'girdle', 'shank1', 'shank2'],
    confirmed: false,
  },
  {
    id: 'signet',
    label: 'Signet',
    what: 'A flat intaglio face carrying the crest. Set with no stones.',
    drops: ['girdle', 'shank1', 'shank2', 'stones'],
    confirmed: false,
  },
  {
    id: 'letterwinner',
    label: 'Letterwinner',
    what: 'Championship geometry with the varsity letter on the face.',
    drops: [],
    confirmed: false,
  },
  {
    id: 'corporate',
    label: 'Corporate',
    what: 'Service and milestone recognition — the crest and the years, not a scoreboard.',
    drops: ['girdle', 'shank1', 'shank2'],
    confirmed: false,
  },
  {
    /* NEW, and it closes a named dead end. `flows.js#FLOW_BLOCKED['sr-grad']`
     * reads: "the Builder has no class-ring style yet … none of those IS a
     * class ring." Once product and style are separate axes, a class ring is
     * simply `ring × class`, so the cell exists. It is still not SELECTABLE —
     * there is no `class` beast — but it is now a named waiting cell with an
     * engine-side item behind it instead of a hole in the model. */
    id: 'class',
    label: 'Class',
    what: 'The scholastic graduation ring. Its own beast — a class ring is not a title ring.',
    drops: [],
    confirmed: false,
  },
  {
    /* Same reasoning: `lib/builders.js#replica-ring` is a real SR product
     * ("$99→$29", MOQ 50) and it was previously only reachable by pointing the
     * Product Builders' replica flow at `champ-ring` — i.e. by quoting a
     * team-store replica as a championship ring. */
    id: 'replica',
    label: 'Replica',
    what: 'The fan / team-store replica. Championship geometry, economy build.',
    drops: [],
    confirmed: false,
  },
]

export const STYLE_BY_ID = Object.fromEntries(STYLES.map((s) => [s.id, s]))

/* ------------------------------------------------------------------ *
 * Which products each beast can actually render
 * ------------------------------------------------------------------ */

/**
 * A beast is a prompt written around a SHAPE. `championship-v1` is a ring
 * prompt — read it: BEZEL, GIRDLE, SIDE SHANK 1 / 2, PALM-INTERIOR, RING SCALE.
 * Handing it `product: 'pendant'` does not make it draw a pendant; it draws the
 * ring and captions it wrongly.
 *
 * The engine does not tell us this, because `/config.styles` ships
 * `{ style, beastId, beastLabel }` and no product coverage. So the default
 * lives here — and `beastProductsFrom()` prefers the config's own answer the
 * moment it ships one, which is the named item in the handover.
 */
export const DEFAULT_BEAST_PRODUCTS = {
  'championship-v1': ['ring'],
}

/**
 * beastId → the products it renders, taking `/config` as the authority when it
 * declares one. A beast the config names with no product coverage is assumed to
 * cover `ring` only — the conservative direction, since the one beast that
 * exists is a ring beast and guessing wider is how a cufflink build comes back
 * as a ring.
 */
export function beastProductsFrom(config) {
  const out = { ...DEFAULT_BEAST_PRODUCTS }
  for (const s of (config && config.styles) || []) {
    const id = String(s.beastId || s.style || '')
    if (!id) continue
    const declared = Array.isArray(s.products) ? s.products.map(String).filter(Boolean) : null
    if (declared && declared.length) out[id] = declared
    else if (!out[id]) out[id] = ['ring']
  }
  return out
}

/* ------------------------------------------------------------------ *
 * The matrix
 * ------------------------------------------------------------------ */

export const BLOCKED = {
  NO_BEAST: 'no-beast',
  BEAST_PRODUCT: 'beast-product',
  NOT_RENDERABLE: 'not-renderable',
}

/**
 * ONE cell of the matrix: may this product be built in this style, and if not,
 * exactly what is missing.
 *
 * @returns {{product,style,available,blocked,reason,beastId,beastLabel,steps:string[]}}
 */
export function cellFor(productId, styleId, config = {}) {
  const p = PRODUCT_BY_ID[productId] || null
  const st = STYLE_BY_ID[styleId] || null
  const declared = (config.styles || []).find((x) => String(x.style || x.id) === styleId) || null
  const beastId = declared ? String(declared.beastId || declared.style || '') : ''
  const beastLabel = declared ? declared.beastLabel || null : null
  const steps = stepKeysFor(productId, styleId)

  const base = { product: productId, style: styleId, beastId: beastId || null, beastLabel, steps }

  if (!p || !st) {
    return { ...base, available: false, blocked: BLOCKED.NO_BEAST, reason: 'Unknown product or style.' }
  }
  if (p.renderable === false) {
    return { ...base, available: false, blocked: BLOCKED.NOT_RENDERABLE, reason: p.blockedReason }
  }
  if (!declared) {
    return {
      ...base,
      available: false,
      blocked: BLOCKED.NO_BEAST,
      reason: `The engine has no beast prompt for the ${st.label} style, so it would refuse the build.`,
    }
  }
  const covers = beastProductsFrom(config)[beastId] || ['ring']
  if (!covers.includes(productId)) {
    return {
      ...base,
      available: false,
      blocked: BLOCKED.BEAST_PRODUCT,
      reason: `${beastLabel || beastId} is written around a ring — bezel, girdle, shanks and palm. It would render a ring and label it a ${p.label.toLowerCase()}.`,
    }
  }
  return { ...base, available: true, blocked: null, reason: '' }
}

/** Every cell, product-major. */
export function matrix(config = {}) {
  return PRODUCTS.map((p) => ({
    product: p,
    cells: STYLES.map((st) => cellFor(p.id, st.id, config)),
  }))
}

/** How many cells can render today, and out of how many. */
export function matrixCoverage(config = {}) {
  let live = 0
  let total = 0
  for (const row of matrix(config)) {
    for (const c of row.cells) { total++; if (c.available) live++ }
  }
  return { live, total }
}

/* ------------------------------------------------------------------ *
 * Step trimming
 * ------------------------------------------------------------------ */

/**
 * The step KEYS this product-in-this-style uses: universal steps, plus the
 * steps the object physically has, minus the ones the style does not use.
 *
 * Order is always the Championship flow's order (Section 4) — a trimmed flow is
 * a SUBSET of the reference flow, never a re-ordering of it, so a rep who knows
 * the ring flow already knows the pendant flow.
 */
export function stepKeysFor(productId, styleId) {
  const p = PRODUCT_BY_ID[productId]
  const st = STYLE_BY_ID[styleId]
  if (!p) return [...ALL_STEP_KEYS]
  const drops = new Set(st ? st.drops : [])
  const has = new Set(p.surfaces)
  return ALL_STEP_KEYS.filter(
    (key) => (UNIVERSAL_STEPS.includes(key) || has.has(key)) && !drops.has(key)
  )
}

/**
 * The step OBJECTS for this cell, renumbered 1..n so the paint-by-numbers
 * property survives trimming — a pendant's flow reads 1 … 8, never
 * "1, 2, 4, 9, 10".
 *
 * @param {Array} steps  the merged step list (config-aware; `steps.js#mergeSteps`)
 */
export function stepsFor(steps, productId, styleId) {
  const keep = new Set(stepKeysFor(productId, styleId))
  /* A step the BACKEND config added is always kept: the engine declared it, so
   * dropping it because this table has never heard of it would silently swallow
   * a real script slot. That is the same rule `mergeSteps` already applies. */
  return (steps || [])
    .filter((s) => keep.has(s.key) || s.fromConfig)
    .map((s, i) => (s.n === i + 1 ? s : { ...s, n: i + 1 }))
}

/**
 * Which steps a cell drops, and why — so the operator can see that a flow got
 * SHORTER on purpose rather than wondering where the shanks went.
 *
 * @returns {Array<{key,title,reason}>}
 */
export function trimmedSteps(productId, styleId) {
  const p = PRODUCT_BY_ID[productId]
  const st = STYLE_BY_ID[styleId]
  const keep = new Set(stepKeysFor(productId, styleId))
  const out = []
  for (const s of CHAMPIONSHIP_STEPS) {
    if (keep.has(s.key)) continue
    const byStyle = st && st.drops.includes(s.key)
    out.push({
      key: s.key,
      title: s.title,
      reason: byStyle
        ? `A ${String(st.label).toLowerCase()} piece does not use it.`
        : `A ${String((p && p.label) || productId).toLowerCase()} does not have one.`,
    })
  }
  return out
}
